const allCountries = require('./countries')
const usStates = require('./usStates')
const canadaProvinces = require('./canadaProvinces')

import CountriesAPI from '@/api/country.api'
import PublicCountriesAPI from '@/api/public-countries.api'
import GlobalSettingsAPI from '@/api/global-settings.api'

// Global setting (string type) holding a JSON array of { code, name } countries
const RESTRICTED_COUNTRIES_SETTING_ID = 'RestrictedCountries'

// Parse and validate the restricted-countries global setting.
// The API returns the string setting as parsed JSON (an array) when the
// response content type is JSON, or as a raw JSON string otherwise.
// Returns a clean [{ code, name }] array, or [] when missing/invalid.
function parseRestrictedCountries(rawValue) {
  if (!rawValue) {
    return []
  }

  let parsed = rawValue

  if (typeof rawValue === 'string') {
    try {
      parsed = JSON.parse(rawValue)
    } catch {
      return []
    }
  }

  if (!Array.isArray(parsed)) {
    return []
  }

  const isValidCountry = country =>
    country &&
    typeof country.code === 'string' && country.code.trim() !== '' &&
    typeof country.name === 'string' && country.name.trim() !== ''

  if (parsed.length === 0 || !parsed.every(isValidCountry)) {
    return []
  }

  return parsed.map(country => ({
    code: country.code.trim(),
    name: country.name.trim()
  }))
}

const state = {
  countries: [],
  selectedRow: {},
  allCountries,
  usStates,
  canadaProvinces,
  //fetch from api
  publicCountries: [],
  // Countries whose applications/accounts are flagged/hidden as restricted.
  // Loaded from the `RestrictedCountries` global setting; empty until fetched/valid.
  restrictedCountries: []

}

const getters = {
  getActiveCountries(state) {
    return state.countries.filter(c => c.active)
  },

  hasRestrictedCountries(state) {
    return state.restrictedCountries.length > 0
  },

  restrictedCountriesLabel(state) {
    return state.restrictedCountries.map(country => country.name).join(', ')
  },

  isRestrictedCountry(state) {
    return value => {
      const normalized = (value || '').toString().trim().toLowerCase()

      if (!normalized) {
        return false
      }

      return state.restrictedCountries.some(country => {
        return normalized === country.code.toLowerCase() || normalized === country.name.toLowerCase()
      })
    }
  }
}

const actions = {
  async list({ commit }) {
    const response = await CountriesAPI.list()
    commit('SET_COUNTRIES', response.data)
  },
  async create(context, newCountry) {
    const response = await CountriesAPI.create(newCountry)
    return response
  },
  async findOne({ commit }, id) {
    const response = await CountriesAPI.findOne(id)
    commit('SET_SELECTED_ROW', response.data)
  },
  async update(context, updatedRow) {
    const response = await CountriesAPI.patch(updatedRow.id, updatedRow)
    return response
  },
  async listAllCountries({ commit }) {
    const response = await PublicCountriesAPI.getAvailableCountries()
    commit('SET_PUBLIC_COUNTRIES', response.data)
  },
  async getRestrictedCountries({ commit }) {
    try {
      const response = await GlobalSettingsAPI.getSettingValue({ type: 'string', settingId: RESTRICTED_COUNTRIES_SETTING_ID })
      commit('SET_RESTRICTED_COUNTRIES', parseRestrictedCountries(response.data))
    } catch {
      commit('SET_RESTRICTED_COUNTRIES', [])
    }
  },
}

const mutations = {
  SET_COUNTRIES(state, countries) {
    state.countries = countries
  },
  SET_SELECTED_ROW(state, row) {
    state.selectedRow = row
  },
  SET_PUBLIC_COUNTRIES(state, countries) {
    state.publicCountries = countries
  },
  SET_RESTRICTED_COUNTRIES(state, countries) {
    state.restrictedCountries = countries
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
